import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Eye, EyeSlash, Key, X } from '@phosphor-icons/react'
import useModal from '../../../Hooks/useModal'
import { useUser } from 'Hooks'
import { Loading } from 'Organisms/Loading'
import { COLOR_BLUE_MATE, GET_COLOR_BY_THEME } from 'constant/colors'
import { ROLE_ADMIN_NAME } from 'constant/roles'
import useSessionStore from 'Context/useSessionStore'

const ROLE_SUPER_ADMIN_NAME = 'super_admin'
const PASSWORD_PATTERN = /(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9])/

const AssignTempPasswordModal = ({ visible, setVisible, selectedUser, reloadUsers = () => {}, onError = () => {} }) => {
  const { user } = useSessionStore((state) => ({ user: state.user }))

  const { Modal, hide } = useModal({ identifier: 'assigntemppassword', visible, setVisible })

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors }
  } = useForm()

  const { loadingUserHook, assignTempPasswordHook } = useUser()

  const [showPassword, setShowPassword] = useState(false)

  const isProtectedTarget = [ROLE_ADMIN_NAME, ROLE_SUPER_ADMIN_NAME].includes(selectedUser?.role)

  useEffect(() => {
    reset({ password: '', confirmPassword: '' })
  }, [selectedUser, reset])

  const onSubmit = async ({ password }) => {
    try {
      if (isProtectedTarget) {
        hide()
        return onError({
          data: {
            open: true,
            error: true,
            message: 'You cannot assign a temporary password to an administrator profile.'
          }
        })
      }

      const res = await assignTempPasswordHook({ userId: selectedUser.id, password })

      hide()

      if (!res.success) {
        return onError({
          data: {
            open: true,
            error: true,
            message: res.message
          }
        })
      }

      reloadUsers()

      return onError({
        data: {
          open: true,
          error: false,
          message: `Temporary password set for ${selectedUser.email}. Share it securely — the user must set a new password at next login.`
        }
      })
    } catch (e) {
      console.error(e)
    }
  }

  return (
    <Modal>
      <>
        {loadingUserHook && <Loading />}

        <div className="modal-content" style={{ maxWidth: 520 }}>
          <div className="modal-header">
            <h3 className="modal-title fs-5" id="assignTempPasswordLabel">
              Assign temporary password
            </h3>
            <p className="modal-close" onClick={hide}>
              <X size={18} color={COLOR_BLUE_MATE} />
            </p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="modal-body p-4">
            <div className="row justify-content-center mb-3">
              <Key size={48} color={GET_COLOR_BY_THEME({ theme: user?.theme }).PRIMARY} weight="bold" />
            </div>

            {isProtectedTarget
              ? (
              <p className="text-center py-3">
                {selectedUser?.firstname} {selectedUser?.lastname} is an administrator profile. Temporary passwords
                cannot be assigned to administrators.
              </p>
                )
              : (
              <>
                <p className="text-center">
                  Set a temporary password for {selectedUser?.firstname} {selectedUser?.lastname} ({selectedUser?.email}).
                  They will be asked to choose a new password the next time they sign in.
                </p>

                <div className="row mb-3">
                  <label className="col-12 col-form-label" htmlFor="inputTempPassword">
                    Temporary password *
                  </label>
                  <div className="col-12">
                    <div className="input-group">
                      <input
                        {...register('password', {
                          required: true,
                          minLength: 8,
                          pattern: PASSWORD_PATTERN
                        })}
                        type={showPassword ? 'text' : 'password'}
                        className={`form-control ${errors && errors.password ? 'is-invalid' : ''}`}
                        id="inputTempPassword"
                        placeholder="Temporary password"
                        autoComplete="new-password"
                      />
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => setShowPassword((prev) => !prev)}
                        aria-label={showPassword ? 'Hide password' : 'Show password'}>
                        {showPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
                      </button>
                      <div className="invalid-feedback">
                        Must be at least 8 characters and include an uppercase letter, a lowercase letter, a number and a
                        symbol.
                      </div>
                    </div>
                  </div>
                </div>

                <div className="row mb-3">
                  <label className="col-12 col-form-label" htmlFor="inputTempPasswordConfirm">
                    Confirm password *
                  </label>
                  <div className="col-12">
                    <input
                      {...register('confirmPassword', {
                        required: true,
                        validate: (value) => value === watch('password')
                      })}
                      type={showPassword ? 'text' : 'password'}
                      className={`form-control ${errors && errors.confirmPassword ? 'is-invalid' : ''}`}
                      id="inputTempPasswordConfirm"
                      placeholder="Confirm password"
                      autoComplete="new-password"
                    />
                    <div className="invalid-feedback">The passwords do not match.</div>
                  </div>
                </div>
              </>
                )}

            <div className="row justify-content-center mt-2">
              <div className="col-auto">
                <button type="button" className="btn btn-secondary mr-2" onClick={hide}>
                  Cancel
                </button>
              </div>
              {!isProtectedTarget && (
                <div className="col-auto">
                  <button type="submit" className="btn btn-primary">
                    Assign password
                  </button>
                </div>
              )}
            </div>
          </form>
        </div>
      </>
    </Modal>
  )
}

export default AssignTempPasswordModal
